<template>
  <v-card>
    <v-toolbar dark color="primary">
      <v-btn icon dark @click="closeDialog(true)">
        <v-icon>mdi-close</v-icon>
      </v-btn>
      <v-toolbar-title>{{ $t('components.ekrm.CreateEkrm.title') }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-toolbar-items>
        <v-btn dark text @click="createReferenceModel">{{ $t('components.ekrm.CreateEkrm.createReferenceModel') }}</v-btn>
      </v-toolbar-items>
    </v-toolbar>
    <v-card-text>
      <v-stepper v-model="step" vertical non-linear>
        <v-stepper-step editable step="1">{{ $t('components.ekrm.CreateEkrm.introduction') }}</v-stepper-step>
          <v-stepper-content step="1">
            <v-container>
              <v-row>
                <v-col cols="12">
                  <h3 class="mb-2">{{ $t('components.ekrm.CreateEkrm.considerationsTitle') }}</h3>
                  <p class="text-justify">{{ $t('components.ekrm.CreateEkrm.introP1') }}</p>
                  <p class="text-justify">{{ $t('components.ekrm.CreateEkrm.introP2') }}</p>
                  <p class="text-justify">{{ $t('components.ekrm.CreateEkrm.introP3') }}</p>
                  <p class="text-justify"><b>{{ $t('components.ekrm.CreateEkrm.introP4Bold') }}</b> {{ $t('components.ekrm.CreateEkrm.introP4') }}</p>
                  <p class="text-right">
                    <v-btn color="primary" @click="step++"><v-icon class="mr-2">mdi-arrow-down-bold</v-icon>{{ $t('components.ekrm.CreateEkrm.nextStep') }}</v-btn>
                  </p>
                </v-col>
              </v-row>
            </v-container>
          </v-stepper-content>
        <v-stepper-step editable step="2">{{ $t('components.ekrm.CreateEkrm.reference') }}</v-stepper-step>
          <v-stepper-content step="2">
            <v-container>
              <v-row>
                <v-col cols="12">
                  <v-alert density="comfortable" type="info" text border="left" color="primary">
                    <ul>
                      <li><b>{{ $t('components.ekrm.CreateEkrm.categoryName') }}:</b> {{ $t('components.ekrm.CreateEkrm.categoryNameInfo') }}</li>
                      <li><b>{{ $t('components.ekrm.CreateEkrm.market') }}:</b> {{ $t('components.ekrm.CreateEkrm.marketInfo') }}</li>
                      <li><b>{{ $t('components.ekrm.CreateEkrm.ownControllableMedia') }}:</b> {{ $t('components.ekrm.CreateEkrm.ownControllableMediaInfo') }}</li>
                    </ul>
                  </v-alert>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="6">
                  <v-text-field ref="textFieldCategory" :label="$t('components.ekrm.CreateEkrm.categoryName')" v-model="reference.category"></v-text-field>
                </v-col>
                <v-col cols="6">
                  <v-select :label="$t('components.ekrm.CreateEkrm.market')" v-model="reference.market" :items="countries" item-text="name" item-value="id"></v-select>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-autocomplete
                    v-model="mediaInternal"
                    :label="$t('components.ekrm.CreateEkrm.ownControllableMedia')"
                    :items="media"
                    dense
                    chips
                    small-chips
                    multiple
                  >
                    <template v-slot:selection="data">
                      <v-chip
                        v-bind="data.attrs"
                        :input-value="data.selected"
                        close
                        @click="data.select"
                        @click:close="removeMedia(data.item)"
                      >
                        {{ data.item }}
                      </v-chip>
                    </template>
                    <template v-slot:append-outer>
                      <v-tooltip top>
                        <template v-slot:activator="{ on }">
                          <v-btn icon color="primary" v-on="on" @click.stop="addMediaDialog = true">
                            <v-icon>mdi-plus-circle</v-icon>
                          </v-btn>
                        </template>
                        {{ $t('components.ekrm.CreateEkrm.addCustomMedia') }}
                      </v-tooltip>
                      <v-dialog v-model="addMediaDialog" max-width="400">
                        <v-card>
                          <v-card-title class="headline">{{ $t('components.ekrm.CreateEkrm.addCustomMedia') }}</v-card-title>
                          <v-card-text>
                            <v-text-field ref="textFieldCustomMedia" :label="$t('components.ekrm.CreateEkrm.customMediaName')" v-model="addCustomMediaName" :rules="customMediaRules"></v-text-field>
                          </v-card-text>
                          <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn color="gray darken-1" text @click="addMediaDialog = false; addCustomMediaName = ''">{{ $t('components.ekrm.CreateEkrm.cancel') }}</v-btn>
                            <v-btn color="red darken-1" text @click="addCustomMedia()" :disabled="addCustomMediaName.length === 0 || !addCustomMediaName.match('^[A-Za-z0-9][A-Za-z0-9 ]*[A-Za-z0-9]$')">{{ $t('components.ekrm.CreateEkrm.add') }}</v-btn>
                          </v-card-actions>
                        </v-card>
                      </v-dialog>
                    </template>
                  </v-autocomplete>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" class="text-right">
                  <v-btn text @click="step--"><v-icon class="mr-2">mdi-arrow-up-bold</v-icon>{{ $t('components.ekrm.CreateEkrm.previousStep') }}</v-btn>
                  <v-tooltip top v-if="reference.media.length === 0">
                    <template v-slot:activator="{ on }">
                      <span v-on="on">
                        <v-btn color="primary" @click="step++" disabled><v-icon class="mr-2">mdi-arrow-down-bold</v-icon>{{ $t('components.ekrm.CreateEkrm.nextStep') }}</v-btn>
                      </span>
                    </template>
                    {{ $t('components.ekrm.CreateEkrm.selectMediaToContinue') }}
                  </v-tooltip>
                  <v-btn v-else color="primary" @click="step++"><v-icon class="mr-2">mdi-arrow-down-bold</v-icon>{{ $t('components.ekrm.CreateEkrm.nextStep') }}</v-btn>
                </v-col>
              </v-row>
            </v-container>
          </v-stepper-content>
        <v-stepper-step :editable="reference.media.length > 0" step="3">{{ $t('components.ekrm.CreateEkrm.mediaSpend') }}</v-stepper-step>
          <v-stepper-content step="3">
            <v-container>
              <v-row>
                <v-col cols="12">
                  <v-alert density="comfortable" type="info" text border="left" color="primary">
                    <ul>
                      <li><b>{{ $t('components.ekrm.CreateEkrm.variationRange') }}:</b> {{ $t('components.ekrm.CreateEkrm.variationRangeInfo') }}</li>
                      <li><b>{{ $t('components.ekrm.CreateEkrm.ownNonControllableMedia') }}:</b> {{ $t('components.ekrm.CreateEkrm.ownNonControllableMediaInfo') }}</li>
                      <li><b>{{ $t('components.ekrm.CreateEkrm.competition') }}:</b> {{ $t('components.ekrm.CreateEkrm.competitionInfo') }}</li>
                    </ul>
                  </v-alert>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <spreadsheet v-model="mediaSpend.ownControllable.data" :columns="mediaSpend.ownControllable.columns" :width="fullWidth" class="mt-2" @input="updatedMediaSpend++" />
                  <spreadsheet v-model="mediaSpend.otherSpend.data" :columns="mediaSpend.otherSpend.columns" :width="fullWidth" class="mt-5" @input="updatedMediaSpend++"  />
                  <river-chart
                    class="mt-5"
                    :data="chartDataMediaSpend"
                    :animation="false"
                    :hide-legend="true"
                    x-name=""
                    :y-name="$t('components.ekrm.CreateEkrm.mediaSpend')"
                    :hide-title="true"
                    icon="chart-bell-curve"
                    :export="false"
                    :colors="chartColorsMediaSpend"
                    :x-axis-type-is-category="true"
                    :x-axis-type-is-date="false"
                    :x-axis-rotate="60"
                    :grid-bottom-margin="65"
                    :sum-one-hundred="false" />
                </v-col>
              </v-row>
              <v-row>
              <v-col cols="12" class="text-right">
                <v-btn text @click="step--"><v-icon class="mr-2">mdi-arrow-up-bold</v-icon>{{ $t('components.ekrm.CreateEkrm.previousStep') }}</v-btn>
                <v-btn color="primary" @click="step++"><v-icon class="mr-2">mdi-arrow-down-bold</v-icon>{{ $t('components.ekrm.CreateEkrm.nextStep') }}</v-btn>
              </v-col>
            </v-row>
            </v-container>
        </v-stepper-content>
        <v-stepper-step :editable="reference.media.length > 0" step="4">{{ $t('components.ekrm.CreateEkrm.decay') }}</v-stepper-step>
        <v-stepper-content step="4">
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-alert density="comfortable" type="info" text border="left" color="primary">
                  <ul>
                    <li><b>{{ $t('components.ekrm.CreateEkrm.minMaxRange') }}:</b> {{ $t('components.ekrm.CreateEkrm.minMaxRangeInfo') }}</li>
                    <li><b>{{ $t('components.ekrm.CreateEkrm.maximumEffect') }}:</b> {{ $t('components.ekrm.CreateEkrm.maximumEffectInfo') }}</li>
                    <li><b>{{ $t('components.ekrm.CreateEkrm.halfLife') }}:</b> {{ $t('components.ekrm.CreateEkrm.halfLifeInfo') }}</li>
                    <li>{{ $t('components.ekrm.CreateEkrm.halfLifeAutoNote') }}</li>
                  </ul>
                </v-alert>
              </v-col>
            </v-row>
            <v-row v-for="medium in reference.media" v-bind:key="`maxEffectMin_${medium}`">
              <v-col cols="2" class="title pr-10" align-self="center" align="right" :style="{ textDecoration: 'underline', textDecorationColor: $vuetify.theme.themes.light.primary }">
                {{ medium }}
              </v-col>
              <v-col cols="8">
                <v-container>
                  <v-row>
                    <v-col cols="5">
                      <decay-range v-if="decay[medium] && decay[medium].maximumEffect" class="pl-5" :title="$t('components.ekrm.CreateEkrm.maximumEffect')" :min="0" :max="2" :value="decay[medium].maximumEffect" @input="updatedDecay($event, 'maximumEffect', medium)"></decay-range>
                    </v-col>
                    <v-col cols="1">&nbsp;</v-col>
                    <v-col cols="5">
                      <decay-range v-if="decay[medium] && decay[medium].halfLife" class="pl-5" :title="$t('components.ekrm.CreateEkrm.halfLife')" :min="minHalfLife[medium]" :max="maxHalfLife[medium]" :value="decay[medium].halfLife" @input="updatedDecay($event, 'halfLife', medium)"></decay-range>
                    </v-col>
                  </v-row>
                </v-container>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" class="text-right mt-5">
                <v-btn text @click="step--"><v-icon class="mr-2">mdi-arrow-up-bold</v-icon>{{ $t('components.ekrm.CreateEkrm.previousStep') }}</v-btn>
                <v-btn color="primary" @click="step++"><v-icon class="mr-2">mdi-arrow-down-bold</v-icon>{{ $t('components.ekrm.CreateEkrm.nextStep') }}</v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-stepper-content>
        <v-stepper-step :editable="reference.media.length > 0" step="5">{{ $t('components.ekrm.CreateEkrm.saturation') }}</v-stepper-step>
        <v-stepper-content step="5">
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-alert density="comfortable" type="info" text border="left" color="primary">
                  <ul>
                    <li>{{ $t('components.ekrm.CreateEkrm.saturationCurvesInfo') }}</li>
                    <li><b>{{ $t('components.ekrm.CreateEkrm.maximumSpend') }}:</b> {{ $t('components.ekrm.CreateEkrm.maximumSpendInfo') }}</li>
                    <li><b>{{ $t('components.ekrm.CreateEkrm.saturationPoint') }}:</b> {{ $t('components.ekrm.CreateEkrm.saturationPointInfo') }}</li>
                    <li><b>{{ $t('components.ekrm.CreateEkrm.revenueVariationRange') }}:</b> {{ $t('components.ekrm.CreateEkrm.revenueVariationRangeInfo') }}</li>
                    <li><b>{{ $t('components.ekrm.CreateEkrm.tenColumnRowLabel') }}:</b> {{ $t('components.ekrm.CreateEkrm.tenColumnRowInfo') }}</li>
                  </ul>
                </v-alert>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <spreadsheet v-if="step === '5' || step === 5" v-model="saturation.data" :columns="saturation.columns" :merge-cells="saturation.mergeCells" :disabled-rows="saturation.disabledRows" :disabled-rows-start-column="4" :disabled-columns="[2]" :width="fullWidth" @input="updatedSaturation++" />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="4" v-for="medium in reference.media" v-bind:key="`satChart_${medium}`">
                <channel-curves-chart-multi
                  :data="chartDataSaturation[medium]"
                  :titleString="medium"
                  :animation="false"
                  :show-legend="false"
                  :x-name="$t('components.ekrm.CreateEkrm.spend')"
                  :y-name="$t('components.ekrm.CreateEkrm.revenue')"
                  :hide-title="false"
                  legend-orient="horizontal"
                  :export="false"
                  :show-negatives="false"
                  :x-axis-type-is-category="false"
                  :x-axis-type-is-date="false"
                  :x-axis-rotate="60"
                  :grid-bottom-margin="50"
                  line-styles='{
                    "Revenue": { "smooth": false, "type": "solid", "width": 2 },
                    "Lower": { "smooth": false, "type": "dashed", "width": 1 },
                    "Higher": { "smooth": false, "type": "dashed", "width": 1 }
                  }' />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" class="text-right">
                <v-btn text @click="step--"><v-icon class="mr-2">mdi-arrow-up-bold</v-icon>{{ $t('components.ekrm.CreateEkrm.previousStep') }}</v-btn>
                <v-btn color="primary" @click="step++"><v-icon class="mr-2">mdi-arrow-down-bold</v-icon>{{ $t('components.ekrm.CreateEkrm.nextStep') }}</v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-stepper-content>
        <v-stepper-step :editable="reference.media.length > 0" step="6">{{ $t('components.ekrm.CreateEkrm.kpi') }}</v-stepper-step>
        <v-stepper-content step="6">
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-alert density="comfortable" type="info" text border="left" color="primary">
                  <ul>
                    <li>{{ $t('components.ekrm.CreateEkrm.kpiRevenueNote') }}</li>
                  </ul>
                </v-alert>
              </v-col>
            </v-row>
              <v-row>
                <v-col cols="12">
                  <spreadsheet v-model="kpi.data" :columns="kpi.columns" :width="fullWidth" @input="updatedKpi++"/>
                  <channel-curves-chart-multi
                    class="mt-5"
                    :data="chartDataKpi"
                    :animation="false"
                    :show-legend="false"
                    :y-name="$t('components.ekrm.CreateEkrm.avgMonthlyRevenue')"
                    :hide-title="true"
                    legend-orient="horizontal"
                    :export="false"
                    :show-negatives="false"
                    :x-axis-type-is-category="true"
                    :x-axis-type-is-date="false"
                    :x-axis-rotate="60"
                    :grid-bottom-margin="65"
                    :disabled-columns="[2]"
                    line-styles='{
                      "KPI": { "smooth": false, "type": "solid", "width": 2 },
                      "Lower": { "smooth": false, "type": "dashed", "width": 1 },
                      "Higher": { "smooth": false, "type": "dashed", "width": 1 }
                    }' />
                </v-col>
              </v-row>
              <v-row>
              <v-col cols="12" class="text-right">
                <v-btn text @click="step--"><v-icon class="mr-2">mdi-arrow-up-bold</v-icon>{{ $t('components.ekrm.CreateEkrm.previousStep') }}</v-btn>
                <v-btn color="primary" @click="step++"><v-icon class="mr-2">mdi-arrow-down-bold</v-icon>{{ $t('components.ekrm.CreateEkrm.nextStep') }}</v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-stepper-content>
        <v-stepper-step :editable="reference.media.length > 0" step="7">{{ $t('components.ekrm.CreateEkrm.seasonalityAndSpotEvents') }}</v-stepper-step>
        <v-stepper-content step="7">
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-alert density="comfortable" type="info" text border="left" color="primary">
                  <ul>
                    <li>{{ $t('components.ekrm.CreateEkrm.seasonalityInfo') }}</li>
                    <li>{{ $t('components.ekrm.CreateEkrm.seasonalityValuesNote') }}</li>
                  </ul>
                </v-alert>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <spreadsheet v-model="seasonalityAndSpotEvents.data" :columns="seasonalityAndSpotEvents.columns" :width="fullWidth" class="mt-2" @input="updatedSeasonalityAndSpotEvents++" />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="5">
                <stacked-bar-chart
                  :data="chartDataSeasonality"
                  :animation="false"
                  :show-legend="false"
                  x-name=""
                  :y-name="$t('components.ekrm.CreateEkrm.percentSalesAttributedPeriodic')"
                  :hide-title="true"
                  :export="false"
                  color="#FB9A99"
                  :x-axis-type-is-category="true"
                  :x-axis-type-is-date="false"
                  :x-axis-rotate="60"
                  :grid-bottom-margin="65" />
              </v-col>
              <v-col cols="5">
                <stacked-bar-chart
                  :data="chartDataSpotEvents"
                  :animation="false"
                  :show-legend="false"
                  x-name=""
                  :y-name="$t('components.ekrm.CreateEkrm.percentSalesAttributedSpot')"
                  :hide-title="true"
                  :export="false"
                  color="#AEC7E8"
                  :x-axis-type-is-category="true"
                  :x-axis-type-is-date="false"
                  :x-axis-rotate="60"
                  :grid-bottom-margin="65" />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" class="text-right">
                <v-btn text @click="step--"><v-icon class="mr-2">mdi-arrow-up-bold</v-icon>{{ $t('components.ekrm.CreateEkrm.previousStep') }}</v-btn>
                <v-btn color="primary" @click="step++"><v-icon class="mr-2">mdi-arrow-down-bold</v-icon>{{ $t('components.ekrm.CreateEkrm.nextStep') }}</v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-stepper-content>
        <v-stepper-step :editable="reference.media.length > 0" step="8">{{ $t('components.ekrm.CreateEkrm.attributions') }}</v-stepper-step>
        <v-stepper-content step="8">
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-alert density="comfortable" type="info" text border="left" color="primary">
                  <ul>
                    <li>{{ $t('components.ekrm.CreateEkrm.attributionsYellowCells') }}</li>
                    <li>{{ $t('components.ekrm.CreateEkrm.mediaSpendContributions') }}</li>
                    <li>{{ $t('components.ekrm.CreateEkrm.minMaxDifference') }}</li>
                    <li>{{ $t('components.ekrm.CreateEkrm.nonControllableNote') }}</li>
                    <li>{{ $t('components.ekrm.CreateEkrm.totalSumNote') }}</li>
                    <li>{{ $t('components.ekrm.CreateEkrm.competitionSpendNote') }}</li>
                  </ul>
                </v-alert>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <spreadsheet v-if="step === '8' || step === 8" :title="$t('components.ekrm.CreateEkrm.percentSalesAttributedByComponent')" v-model="attributions.data" :columns="attributions.columns" :disabled-rows="attributions.disabledRows" :disabled-rows-start-column="1" width="100%" class="mt-5" @input="updatedAttributionsHandler" />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" class="text-right">
                <v-btn text @click="step--"><v-icon class="mr-2">mdi-arrow-up-bold</v-icon>{{ $t('components.ekrm.CreateEkrm.previousStep') }}</v-btn>
                <v-btn color="primary" @click="step++"><v-icon class="mr-2">mdi-arrow-down-bold</v-icon>{{ $t('components.ekrm.CreateEkrm.nextStep') }}</v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-stepper-content>
        <v-stepper-step :editable="reference.media.length > 0" step="9">{{ $t('components.ekrm.CreateEkrm.futureTrends') }}</v-stepper-step>
        <v-stepper-content step="9">
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-alert density="comfortable" type="info" text border="left" color="primary">
                  <ul>
                    <li>{{ $t('components.ekrm.CreateEkrm.futureTrendsInfo') }}</li>
                  </ul>
                </v-alert>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <spreadsheet :title="$t('components.ekrm.CreateEkrm.expectedGrowthRate')" v-model="futureTrends.data" :columns="futureTrends.columns" width="100%" class="mt-5" />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" class="text-right">
                <v-btn text @click="step--"><v-icon class="mr-2">mdi-arrow-up-bold</v-icon>{{ $t('components.ekrm.CreateEkrm.previousStep') }}</v-btn>
                <v-btn color="primary" @click="createReferenceModel"><v-icon class="mr-2">mdi-check-bold</v-icon>{{ $t('components.ekrm.CreateEkrm.createReferenceModel') }}</v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-stepper-content>
      </v-stepper>
      <v-dialog v-model="errorDialog" width="80%">
        <v-card>
          <v-card-title>
            <span class="h3 mb-3">{{ $t('components.ekrm.CreateEkrm.errorsAndWarnings') }}</span>
          </v-card-title>
          <v-card-text>
            <v-alert dense v-for="(error, index) in errors" v-bind:key="`error_${index}`" :type="error.type" text border="left">
              <b>[<span v-for="(section, sectionIndex) in error.sections" v-bind:key="`errorSection_${section}_${index}`">{{ section }}. {{ sections[section - 1] }}<span v-if="sectionIndex < error.sections.length - 1"> & </span></span>]</b> {{ error.text }}
            </v-alert>
            <v-alert dense v-for="(error, index) in warnings" v-bind:key="`warning_${index}`" :type="error.type" text border="left">
              <b>[<span v-for="(section, sectionIndex) in error.sections" v-bind:key="`errorSection_${section}_${index}`">{{ section }}. {{ sections[section - 1] }}<span v-if="sectionIndex < error.sections.length - 1"> & </span></span>]</b> {{ error.text }}
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="error"
              text
              @click="errorDialog = false"
            >
              {{ $t('components.ekrm.CreateEkrm.goBackAndFix') }}
            </v-btn>
            <v-btn
              v-if="errors.length === 0"
              color="primary"
              text
              @click="errorDialog = false; saveJSON(); closeDialog(false)"
            >
              {{ $t('components.ekrm.CreateEkrm.ignoreWarnings') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import Spreadsheet from '@/components/ekrm/Spreadsheet.vue'
import DecayRange from '@/components/ekrm/DecayRange.vue'
import RiverChart from '@/components/RiverChart.vue'
import ChannelCurvesChartMulti from '@/components/simulationDetails/ChannelCurvesChartMulti.vue'
import StackedBarChart from '@/components/StackedBarChart.vue'
import { monthTitlesFor } from '@/utils/ekrmDefinition.js'

export default {
  data () {
    return {
      mediaInternal: [],
      draft: null,
      // The calendar months a reopened EKRM's grid columns stand for, as 'YYYY-MM' — written by
      // the retired clone wizard when it copied a model over more than one year. Null for anything
      // built here, and for every EKRM saved outside that wizard, in which case the twelve bare
      // month names are the right titles. Stored rather than rendered so a document made in one
      // language reopens in the reader's own.
      months: null,
      minHalfLife: {},
      maxHalfLife: {},
      errorDialog: false,
      updatedMediaSpend: 0,
      updatedSaturation: 0,
      updatedSeasonalityAndSpotEvents: 0,
      updatedKpi: 0,
      errors: [],
      warnings: [],
      colors: [
        '#A6CEE3',
        '#B2DF8A',
        '#FB9A99',
        '#FDBF6F',
        '#CAB2D6',
        '#B15928',
        '#1F78B4',
        '#33A02C',
        '#E31A1C',
        '#FF7F00',
        '#6A3D9A',
        '#1F77B4',
        '#FF7F0E',
        '#2CA02C',
        '#D62728',
        '#9467BD',
        '#8C564B',
        '#E377C2',
        '#7F717F',
        '#BCBD22',
        '#17BECF',
        '#AEC7E8',
        '#FFBB78',
        '#98DF8A',
        '#FF9896',
        '#C49C94',
        '#F7B6D2',
        '#A6CEE3',
        '#B2DF8A',
        '#FB9A99',
        '#FDBF6F',
        '#CAB2D6',
        '#B15928',
        '#1F78B4',
        '#33A02C',
        '#E31A1C',
        '#FF7F00',
        '#6A3D9A',
        '#1F77B4',
        '#FF7F0E',
        '#2CA02C',
        '#D62728',
        '#9467BD',
        '#8C564B',
        '#E377C2',
        '#7F717F',
        '#BCBD22',
        '#17BECF',
        '#AEC7E8',
        '#FFBB78',
        '#98DF8A',
        '#FF9896',
        '#C49C94',
        '#F7B6D2',
        '#A6CEE3',
        '#B2DF8A',
        '#FB9A99',
        '#FDBF6F',
        '#CAB2D6',
        '#B15928',
        '#1F78B4',
        '#33A02C',
        '#E31A1C',
        '#FF7F00',
        '#6A3D9A',
        '#1F77B4',
        '#FF7F0E',
        '#2CA02C',
        '#D62728',
        '#9467BD',
        '#8C564B',
        '#E377C2',
        '#7F717F',
        '#BCBD22',
        '#17BECF',
        '#AEC7E8',
        '#FFBB78',
        '#98DF8A',
        '#FF9896',
        '#C49C94',
        '#F7B6D2',
        '#A6CEE3',
        '#B2DF8A',
        '#FB9A99',
        '#FDBF6F',
        '#CAB2D6',
        '#B15928',
        '#1F78B4',
        '#33A02C',
        '#E31A1C',
        '#FF7F00',
        '#6A3D9A',
        '#1F77B4',
        '#FF7F0E',
        '#2CA02C',
        '#D62728',
        '#9467BD',
        '#8C564B',
        '#E377C2',
        '#7F717F',
        '#BCBD22',
        '#17BECF',
        '#AEC7E8',
        '#FFBB78',
        '#98DF8A',
        '#FF9896',
        '#C49C94',
        '#F7B6D2',
        '#A6CEE3',
        '#B2DF8A',
        '#FB9A99',
        '#FDBF6F',
        '#CAB2D6',
        '#B15928',
        '#1F78B4',
        '#33A02C',
        '#E31A1C',
        '#FF7F00',
        '#6A3D9A',
        '#1F77B4',
        '#FF7F0E',
        '#2CA02C',
        '#D62728',
        '#9467BD',
        '#8C564B',
        '#E377C2',
        '#7F717F',
        '#BCBD22',
        '#17BECF',
        '#AEC7E8',
        '#FFBB78',
        '#98DF8A',
        '#FF9896',
        '#C49C94',
        '#F7B6D2'
      ],
      addCustomMediaName: '',
      addMediaDialog: false,
      step: 1,
      media: ['Audio', 'Cinema', 'CTA', 'Display', 'Email Marketing', 'Events', 'FSI', 'Gaming', 'Influencers', 'Instore', 'Local TV', 'Magazines', 'Mailing', 'Newspapers', 'Online Video', 'OOH', 'Open TV', 'Pay TV', 'Radio', 'Mobile', 'Programmatic', 'Search', 'Social Media'],
      reference: {
        category: '',
        tier: 'Reference 1',
        market: '',
        media: []
      },
      futureTrends: {
        columns: [
          { type: 'html', title: ' ', width: '450px', readOnly: true, wordWrap: true, align: 'left' },
          { type: 'numeric', title: 'Min', width: '200px', decimal: '.', allowEmpty: true, customFormat: '%', allowNegatives: true },
          { type: 'numeric', title: 'Max', width: '200px', decimal: '.', allowEmpty: true, customFormat: '%', allowNegatives: true }
        ],
        data: [
          ['Sales (Revenue)'],
          ['Own Controllable Media Spend (Total)'],
          ['Own Non-Controllable Media/Co-Marketing Spend (Total)'],
          ['Competition Spend (Total)']
        ]
      },
      attributions: {
        columns: [
          { type: 'html', title: ' ', width: '450px', readOnly: true, wordWrap: true, align: 'left' },
          { type: 'numeric', title: 'Min', width: '200px', decimal: '.', allowEmpty: true, customFormat: '%', allowNegativesRows: ['2'] },
          { type: 'numeric', title: 'Max', width: '200px', decimal: '.', allowEmpty: true, customFormat: '%', allowNegativesRows: ['2'] }
        ],
        data: [
          ['Own Controllable Media Spend (Total)'],
          ['Own Non-Controllable Media/Co-Marketing Spend (Total)'],
          ['Competition Spend (Total)'],
          ['Periodic/Cyclic Events'],
          ['Spot Events'],
          ['Base']
        ],
        disabledRows: [3, 4, 5]
      },
      seasonalityAndSpotEvents: {
        data: [
          ['% of Sales Attributed to Periodic/Cyclic Events'],
          ['% of Sales Attributed to Spot Events']
        ],
        columns: [
          { type: 'html', title: ' ', width: '350px', readOnly: true, wordWrap: true, align: 'left' },
          { type: 'numeric', title: 'Variation Range (+/- %)', width: '200px', decimal: '.', allowEmpty: true, customFormat: '%' },
          { type: 'numeric', title: 'January', width: '100px', decimal: '.', allowEmpty: true, customFormat: '%', allowNegatives: true },
          { type: 'numeric', title: 'February', width: '100px', decimal: '.', allowEmpty: true, customFormat: '%', allowNegatives: true },
          { type: 'numeric', title: 'March', width: '100px', decimal: '.', allowEmpty: true, customFormat: '%', allowNegatives: true },
          { type: 'numeric', title: 'April', width: '100px', decimal: '.', allowEmpty: true, customFormat: '%', allowNegatives: true },
          { type: 'numeric', title: 'May', width: '100px', decimal: '.', allowEmpty: true, customFormat: '%', allowNegatives: true },
          { type: 'numeric', title: 'June', width: '100px', decimal: '.', allowEmpty: true, customFormat: '%', allowNegatives: true },
          { type: 'numeric', title: 'July', width: '100px', decimal: '.', allowEmpty: true, customFormat: '%', allowNegatives: true },
          { type: 'numeric', title: 'August', width: '100px', decimal: '.', allowEmpty: true, customFormat: '%', allowNegatives: true },
          { type: 'numeric', title: 'September', width: '100px', decimal: '.', allowEmpty: true, customFormat: '%', allowNegatives: true },
          { type: 'numeric', title: 'October', width: '100px', decimal: '.', allowEmpty: true, customFormat: '%', allowNegatives: true },
          { type: 'numeric', title: 'November', width: '100px', decimal: '.', allowEmpty: true, customFormat: '%', allowNegatives: true },
          { type: 'numeric', title: 'December', width: '100px', decimal: '.', allowEmpty: true, customFormat: '%', allowNegatives: true }
        ]
      },
      kpi: {
        data: [
          ['Avg. Monthly Revenue (USD)']
        ],
        columns: [
          { type: 'html', title: ' ', width: '250px', readOnly: true, wordWrap: true, align: 'left' },
          { type: 'numeric', title: 'Variation Range (+/- %)', width: '200px', decimal: '.', allowEmpty: true, customFormat: '%' },
          { type: 'numeric', title: 'January', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
          { type: 'numeric', title: 'February', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
          { type: 'numeric', title: 'March', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
          { type: 'numeric', title: 'April', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
          { type: 'numeric', title: 'May', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
          { type: 'numeric', title: 'June', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
          { type: 'numeric', title: 'July', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
          { type: 'numeric', title: 'August', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
          { type: 'numeric', title: 'September', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
          { type: 'numeric', title: 'October', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
          { type: 'numeric', title: 'November', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
          { type: 'numeric', title: 'December', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' }
        ]
      },
      saturation: {
        columns: [
          { type: 'html', title: 'Media', width: '200px', readOnly: true, wordWrap: true, align: 'left' },
          { type: 'numeric', title: 'Revenue Variation Range', width: '200px', decimal: '.', allowEmpty: true, customFormat: '%' },
          { type: 'numeric', title: `Maximum Spend`, width: '120px', decimal: '.', allowEmpty: true, customFormat: '$' },
          { type: 'numeric', title: `Saturation Point`, width: '120px', decimal: '.', allowEmpty: true, customFormat: '$' },
          { type: 'numeric', title: '1', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
          { type: 'numeric', title: '2', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
          { type: 'numeric', title: '3', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
          { type: 'numeric', title: '4', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
          { type: 'numeric', title: '5', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
          { type: 'numeric', title: '6', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
          { type: 'numeric', title: '7', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
          { type: 'numeric', title: '8', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
          { type: 'numeric', title: '9', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
          { type: 'numeric', title: '10', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' }
        ],
        disabledRows: [],
        data: []
      },
      decay: {},
      mediaSpend: {
        ownControllable: {
          columns: [
            { type: 'html', title: 'Own Controllable Media', width: '200px', readOnly: true, wordWrap: true, align: 'left' },
            { type: 'numeric', title: 'Variation Range (+/- %)', width: '200px', decimal: '.', allowEmpty: true, customFormat: '%' },
            { type: 'numeric', title: 'January', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
            { type: 'numeric', title: 'February', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
            { type: 'numeric', title: 'March', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
            { type: 'numeric', title: 'April', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
            { type: 'numeric', title: 'May', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
            { type: 'numeric', title: 'June', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
            { type: 'numeric', title: 'July', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
            { type: 'numeric', title: 'August', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
            { type: 'numeric', title: 'September', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
            { type: 'numeric', title: 'October', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
            { type: 'numeric', title: 'November', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
            { type: 'numeric', title: 'December', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' }
          ],
          data: []
        },
        otherSpend: {
          columns: [
            { type: 'html', title: 'Other', width: '200px', readOnly: true, wordWrap: true, align: 'left' },
            { type: 'numeric', title: 'Variation Range (+/- %)', width: '200px', decimal: '.', allowEmpty: true, customFormat: '%' },
            { type: 'numeric', title: 'January', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
            { type: 'numeric', title: 'February', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
            { type: 'numeric', title: 'March', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
            { type: 'numeric', title: 'April', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
            { type: 'numeric', title: 'May', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
            { type: 'numeric', title: 'June', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
            { type: 'numeric', title: 'July', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
            { type: 'numeric', title: 'August', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
            { type: 'numeric', title: 'September', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
            { type: 'numeric', title: 'October', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
            { type: 'numeric', title: 'November', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' },
            { type: 'numeric', title: 'December', width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' }
          ],
          data: [
            ['Own Non-Controllable Media/Co-Marketing (Total)'],
            ['Competition (Total)']
          ]
        }
      }
    }
  },
  methods: {
    ...mapActions('manager', [
      'createEkrm'
    ]),
    updatedDecay (value, decayType, medium) {
      this.$set(this.decay[medium], decayType, value)
      if (decayType === 'maximumEffect') {
        const min = value[0]
        const max = value[1]
        this.$set(this.minHalfLife, medium, min)
        this.$set(this.maxHalfLife, medium, max + 1)
      }
    },
    validateAll () {
      this.errors = []
      this.warnings = []
      // Category
      if (!this.reference.category || this.reference.category.length === 0) {
        this.errors.push({ type: 'error', sections: [2], text: 'Category name is required.' })
      }
      // Market
      if (!this.reference.market || this.reference.market.length === 0) {
        this.errors.push({ type: 'error', sections: [2], text: 'Market is required.' })
      }
      // Inversión vs. Ventas Anuales - sum(Inversión) <= sum(Ventas) para el mismo año
      const totalKPI = this.kpi.data[0].slice(2).reduce((partialSum, a) => Number(partialSum) + Number(a), 0)
      // const totalMediaSpendOtherComarketing = this.mediaSpend.otherSpend.data[0].slice(2).reduce((partialSum, a) => Number(partialSum) + Number(a), 0)
      // const totalMediaSpendOtherCompetition = this.mediaSpend.otherSpend.data[1].slice(2).reduce((partialSum, a) => Number(partialSum) + Number(a), 0)
      let totalMediaSpendOwn = 0
      for (const medium of this.mediaSpend.ownControllable.data) {
        totalMediaSpendOwn += medium.slice(2).reduce((partialSum, a) => Number(partialSum) + Number(a), 0)
      }
      if (totalKPI <= totalMediaSpendOwn) {
        this.warnings.push({ type: 'warning', sections: [3, 6], text: 'Total Revenue should be larger than Total Own Media Spend.' })
      }
      // atribución componentes - el valor mínimo y máximo debiesen ser distintos, al menos 3% (puede cambiar el valor) de diferencia
      if (this.attributions.data[0][2] !== '' && this.attributions.data[0][1] !== '' && Math.abs(Number(this.attributions.data[0][2]) - Number(this.attributions.data[0][1])) < 3) {
        this.warnings.push({ type: 'warning', sections: [8], text: 'Min and max values should be farther apart (at least 3%) for Own Controllable Media Spend.' })
      }
      if (this.attributions.data[1][2] !== '' && this.attributions.data[1][1] !== '' && Math.abs(Number(this.attributions.data[1][2]) - Number(this.attributions.data[1][1])) < 3) {
        this.warnings.push({ type: 'warning', sections: [8], text: 'Min and max values should be farther apart (at least 3%) for Own Non-Controllable Media/Co-Marketing Spend.' })
      }
      if (this.attributions.data[2][2] !== '' && this.attributions.data[2][1] !== '' && Math.abs(Number(this.attributions.data[2][2]) - Number(this.attributions.data[2][1])) < 3) {
        this.warnings.push({ type: 'warning', sections: [8], text: 'Min and max values should be farther apart (at least 3%) for Competition Spend.' })
      }
      // atribución componentes - Competition Spend attribution cannot be positive
      if (this.attributions.data[2][2] !== '' && this.attributions.data[2][1] !== '' && (Number(this.attributions.data[2][2]) > 0 || Number(this.attributions.data[2][1]) > 0)) {
        this.errors.push({ type: 'error', sections: [8], text: 'Competition Spend attribution cannot be positive.' })
      }
      // atribución componentes - no tener atribuciones para componentes que no se tenga inversión. Por ejemplo no tener inversión en comarketing pero esperar que tenga una atribución distinta a 0
      /* if (totalMediaSpendOtherComarketing === 0 && (Number(this.attributions.data[1][1]) > 0 || Number(this.attributions.data[1][1]) < 0 || Number(this.attributions.data[1][2]) > 0 || Number(this.attributions.data[1][2]) < 0)) {
        this.warnings.push({ type: 'warning', sections: [3, 8], text: 'Own Non-Controllable Media/Co-Marketing Spend cannot have an attribution value if there is no spend for that component. This component will not be considered.' })
      }
      if (totalMediaSpendOtherCompetition === 0 && (Number(this.attributions.data[2][1]) > 0 || Number(this.attributions.data[2][1]) < 0 || Number(this.attributions.data[2][2]) > 0 || Number(this.attributions.data[2][2]) < 0)) {
        this.warnings.push({ type: 'warning', sections: [3, 8], text: 'Competition Spend cannot have an attribution value if there is no spend for that component. This component will not be considered.' })
      } */
      // Consistencia de medios - Los medios declarados en la hoja de Media Spend deberían ser los mismos que aparecen en Saturation. En caso de que un medio no esté, no se podrá considerar en el modelo.
      for (let index = 0; index < this.reference.media.length; index++) {
        /* let medium = this.decay.maximumEffect.data[index]
        if (medium.slice(1).some(column => column === '' || isNaN(Number(column)))) {
          this.warnings.push({ type: 'warning', sections: [4], text: `${medium[0]} is missing Decay Maximum Effect data, and will not be considered.` })
        }
        medium = this.decay.halfLife.data[index]
        if (medium.slice(1).some(column => column === '' || isNaN(Number(column)))) {
          this.warnings.push({ type: 'warning', sections: [4], text: `${medium[0]} is missing Decay Half Life data, and will not be considered.` })
        } */
        let medium = this.saturation.data[2 * index]
        if ((medium[1] === '' || isNaN(Number(medium[1])) || medium[3] === '' || isNaN(Number(medium[3])))) {
          console.log('Medium:')
          console.log(medium)
          this.errors.push({ type: 'error', sections: [5], text: `${this.reference.media[index]} is missing Saturation data.` })
        }
        medium = this.saturation.data[2 * index + 1]
        if (medium.slice(4).some(column => column === '' || isNaN(Number(column)))) {
          this.errors.push({ type: 'error', sections: [5], text: `${this.reference.media[index]} is missing Saturation Curve data.` })
        }
        medium = this.mediaSpend.ownControllable.data[index]
        if (medium[1] === '' || isNaN(Number(medium[1]))) {
          this.errors.push({ type: 'error', sections: [3], text: `${medium[0]} is missing the Variation Range.` })
        }
        if (medium.slice(2).every(column => column === '' || isNaN(Number(column)))) {
          this.errors.push({ type: 'error', sections: [3], text: `${medium[0]} is missing data for every month.` })
        } else if (medium.slice(2).some(column => column === '' || isNaN(Number(column)))) {
          this.warnings.push({ type: 'warning', sections: [3], text: `${medium[0]} is missing data, missing datapoints will be completed with zeros.` })
        }
      }
      // Other spend (Co-Marketing and Competition)
      let medium = this.mediaSpend.otherSpend.data[0]
      if ((medium.slice(2).some(column => column !== '' && !isNaN(Number(column)))) && (medium.slice(2).some(column => column === '' || isNaN(Number(column))))) {
        this.warnings.push({ type: 'warning', sections: [3], text: `${medium[0]} is missing data, missing datapoints will be completed with zeros.` })
        if (medium[1] === '' || isNaN(Number(medium[1]))) {
          this.errors.push({ type: 'error', sections: [3], text: `${medium[0]} is missing the Variation Range.` })
        }
      }
      medium = this.mediaSpend.otherSpend.data[1]
      if ((medium.slice(2).some(column => column !== '' && !isNaN(Number(column)))) && (medium.slice(2).some(column => column === '' || isNaN(Number(column))))) {
        this.warnings.push({ type: 'warning', sections: [3], text: `${medium[0]} is missing data, missing datapoints will be completed with zeros.` })
        if (medium[1] === '' || isNaN(Number(medium[1]))) {
          this.errors.push({ type: 'error', sections: [3], text: `${medium[0]} is missing the Variation Range.` })
        }
      }
      // Consistencia de componentes - Las componentes declaradas en la hoja de Media Spend (competition y comarketing) con datos, deberían aparecer también con datos en las hojas de Attributions y Future Trends. En caso de que una componente no tenga datos en alguna de estas hojas pero que si tenga en algunas otras, no se podrá considerar en el modelo.
      const comarketingMissingDataInMediaSpend = this.mediaSpend.otherSpend.data[0].slice(1).some(column => column === '' || isNaN(Number(column)))
      const competitionMissingDataInMediaSpend = this.mediaSpend.otherSpend.data[1].slice(1).some(column => column === '' || isNaN(Number(column)))
      const comarketingMissingDataInAttributions = this.attributions.data[1].slice(1).some(column => column === '' || isNaN(Number(column)))
      const competitionMissingDataInAttributions = this.attributions.data[2].slice(1).some(column => column === '' || isNaN(Number(column)))
      const comarketingMissingDataInFutureTrends = this.futureTrends.data[2].slice(1).some(column => column === '' || isNaN(Number(column)))
      const competitionMissingDataInFutureTrends = this.futureTrends.data[3].slice(1).some(column => column === '' || isNaN(Number(column)))
      if (!comarketingMissingDataInMediaSpend || !comarketingMissingDataInAttributions || !comarketingMissingDataInFutureTrends) {
        if (comarketingMissingDataInMediaSpend) {
          this.warnings.push({ type: 'warning', sections: [3], text: 'Comarketing is missing data in Media Spend, and will not be considered.' })
        }
        if (comarketingMissingDataInAttributions) {
          this.warnings.push({ type: 'warning', sections: [8], text: 'Comarketing is missing data in Attributions. The model will choose proper values between 0% and 100%.' })
        }
        if (comarketingMissingDataInFutureTrends) {
          this.warnings.push({ type: 'warning', sections: [9], text: 'Comarketing is missing data in Future Trends. The model will choose proper values between 0% and 100%.' })
        }
      }
      if (!competitionMissingDataInMediaSpend || !competitionMissingDataInAttributions || !competitionMissingDataInFutureTrends) {
        if (competitionMissingDataInMediaSpend) {
          this.warnings.push({ type: 'warning', sections: [3], text: 'Competition is missing data in Media Spend, and will not be considered.' })
        }
        if (competitionMissingDataInAttributions) {
          this.warnings.push({ type: 'warning', sections: [8], text: 'Competition is missing data in Attributions. The model will choose proper values between -100% and 0%.' })
        }
        if (competitionMissingDataInFutureTrends) {
          this.warnings.push({ type: 'warning', sections: [9], text: 'Competition is missing data in Future Trends. The model will choose proper values between -100% and 0%.' })
        }
      }
      // Variation ranges en inversión de medios - Que las variation ranges declaradas no sean mayores al 50% (?), dado que esto reflejaría mayor incertidumbre en el modelo.
      for (const item of this.mediaSpend.ownControllable.data) {
        if (Number(item[1]) >= 50) {
          this.warnings.push({ type: 'warning', sections: [3], text: 'Variation Ranges for Own Controllable Media Spend should be less than 50%.' })
          break
        }
      }
      for (const item of this.mediaSpend.otherSpend.data) {
        if (Number(item[1]) >= 50) {
          this.warnings.push({ type: 'warning', sections: [3], text: 'Variation Ranges for Other Spend should be less than 50%.' })
          break
        }
      }
      // Variation ranges en saturaciones - Que las variation ranges declaradas no sean mayores al 50% (?), dado que esto reflejaría mayor incertidumbre en el modelo.
      for (const [index, item] of this.saturation.data.entries()) {
        if (index % 2 === 0 && Number(item[1]) >= 50) {
          this.warnings.push({ type: 'warning', sections: [5], text: 'Variation Ranges for Saturations should be less than 50%.' })
          break
        }
      }
      // Consistencia de inversión vs saturaciones - Que la inversión máxima no sea muy chica respecto de la grilla provista (menor al 50% del punto de saturación)
      // Consistencia de inversión vs saturaciones - Que la inversión máxima no sea muy grande respecto de la grilla provista (mayor al doble de la saturación)
      for (const [index, item] of this.saturation.data.entries()) {
        const maxSpend = this.maxOwnControllableMediaSpends[index]
        const saturationPoint = Number(item[3])
        if (index % 2 === 0) {
          if (saturationPoint === 0) {
            this.errors.push({ type: 'error', sections: [5], text: `Saturation Point must be > 0 for ${item[0]}` })
            break
          }
          if (2 * maxSpend < saturationPoint || maxSpend > 2 * saturationPoint) {
            this.warnings.push({ type: 'warning', sections: [5], text: `Saturation Point should be between half and double of Maximum Spend for ${item[0]}` })
            break
          }
        }
      }
      // Variation ranges en KPI - Que las variation ranges declaradas no sean mayores al 50% (?), dado que esto reflejaría mayor incertidumbre en el modelo.
      if (Number(this.kpi.data[0][1]) >= 50) {
        this.warnings.push({ type: 'warning', sections: [6], text: 'Variation Range for KPI should be less than 50%.' })
      } else if (this.kpi.data[0][1] === '' || isNaN(Number(this.kpi.data[0][1]))) {
        this.errors.push({ type: 'error', sections: [6], text: `${this.kpi.data[0][0]} is missing the Variation Range.` })
      }
      // atribucion componentes - Atribución de marketing > 0. Suma de atribuciones de mínimos (sin contar Base) < 100
      let sumAttributions = 0
      for (const component of this.attributions.data) {
        if (component[0] !== 'Base') {
          sumAttributions += Number(component[1])
        }
        // atribución componentes - Valores Min y Max de atribución para un componente no pueden ser iguales
        const fixedComponents = ['Base', 'Spot Events', 'Periodic/Cyclic Events']
        if (!fixedComponents.includes(component[0]) && (component[1] !== '') && (component[1] === component[2])) {
          this.errors.push({ type: 'error', sections: [8], text: `Min and Max attribution values cannot be the same for component ${component[0]}` })
        }
        if (!fixedComponents.includes(component[0]) && ((component[1] === '') !== (component[2] === ''))) {
          this.errors.push({ type: 'error', sections: [8], text: `Min and Max attribution values must either both or none be filled-in for component ${component[0]}` })
        }
      }
      if (sumAttributions >= 100) {
        this.errors.push({ type: 'error', sections: [8], text: 'Sum of all attribution Min values (except Base) must be less than 100' })
      }
      if (Number(this.attributions.data[0][1]) <= 0 || Number(this.attributions.data[0][2]) <= 0) {
        this.errors.push({ type: 'error', sections: [8], text: 'Marketing attribution must be positive' })
      }
      // atribución componentes - el valor mínimo debe ser menor o igual que el máximo
      for (const component of this.attributions.data) {
        console.log(component)
        if (Number(component[2]) < Number(component[1])) {
          this.errors.push({ type: 'error', sections: [8], text: 'Min values cannot be larger than max values.' })
          break
        }
      }
      // Saturaciones - La curva S de cada medio debe ser creciente
      for (const [index, medium] of this.saturation.data.entries()) {
        if (index % 2 === 1) {
          let prev = -1
          for (const value of medium.slice(4)) {
            if (Number(value) < prev) {
              this.errors.push({ type: 'error', sections: [5], text: `Decreasing saturation for ${this.saturation.data[index - 1][0]} is not allowed.` })
              break
            }
            prev = Number(value)
          }
          // Convergencia de saturaciones - Que la curva haya convergido hacia el final de la grilla. La regla puede ser (S_{n} - S_{n-1}) / S_{n} <= 0.03  (3%) donde S_{n} es el valor de la saturación en el último punto de la grilla.
          const last = Number(medium[13])
          const beforeLast = Number(medium[12])
          if ((last - beforeLast) / last >= 0.03) {
            this.warnings.push({ type: 'warning', sections: [5], text: `Saturation should converge at the end of the grid (second-to-last and last values should be closer) for ${this.saturation.data[index - 1][0]}.` })
          }
        }
      }
      // Seasonality and spot events - Valores de atribución por mes, tanto para periodic como para spot events, deben estar entre -100% y %100
      for (const value of this.seasonalityAndSpotEvents.data[0].slice(1)) {
        const val = Number(value)
        if (Math.abs(val) > 100) {
          this.errors.push({ type: 'error', sections: [7], text: 'Each of the Periodic/Cyclic Events values must be between -100% and 100%.' })
          break
        }
      }
      for (const value of this.seasonalityAndSpotEvents.data[1].slice(1)) {
        const val = Number(value)
        if (Math.abs(val) > 100) {
          this.errors.push({ type: 'error', sections: [7], text: 'Each of the Spot Events values must be between -100% and 100%.' })
          break
        }
      }
      // Seasonality and spot events - La suma de los valores de atribución por mes de periodic y spot events deben estar entre -100% y %100
      for (let index = 0; index < this.seasonalityAndSpotEvents.data[0].length; index++) {
        if (index > 1 && Math.abs(Number(this.seasonalityAndSpotEvents.data[0][index]) + Number(this.seasonalityAndSpotEvents.data[1][index])) > 100) {
          this.errors.push({ type: 'error', sections: [7], text: 'Sum of Periodic/Cyclic + Spot Events must be between -100% and 100% for each month.' })
          break
        }
      }
      // Seasonality and spot events - Variaton ranges are mandatory when there is data in any column
      if ((this.seasonalityAndSpotEvents.data[0].slice(2).some(column => column !== '' && !isNaN(Number(column))))) {
        if (this.seasonalityAndSpotEvents.data[0][1] === '' || isNaN(Number(this.seasonalityAndSpotEvents.data[0][1]))) {
          this.errors.push({ type: 'error', sections: [7], text: `${this.seasonalityAndSpotEvents.data[0][0]} is missing the Variation Range.` })
        }
      }
      if ((this.seasonalityAndSpotEvents.data[1].slice(2).some(column => column !== '' && !isNaN(Number(column))))) {
        if (this.seasonalityAndSpotEvents.data[1][1] === '' || isNaN(Number(this.seasonalityAndSpotEvents.data[1][1]))) {
          this.errors.push({ type: 'error', sections: [7], text: `${this.seasonalityAndSpotEvents.data[1][0]} is missing the Variation Range.` })
        }
      }
      // Decay - Tanto moda como half-life deben ser valores enteros
      /* for (const item of this.decay.maximumEffect.data) {
        if (!Number.isInteger(Number(item[1])) || !Number.isInteger(Number(item[2]))) {
          this.errors.push({ type: 'error', sections: [4], text: 'Maximum Effect values must be integers.' })
          break
        }
      }
      for (const item of this.decay.halfLife.data) {
        if (!Number.isInteger(Number(item[1])) || !Number.isInteger(Number(item[2]))) {
          this.errors.push({ type: 'error', sections: [4], text: 'Half-Life values must be integers.' })
          break
        }
      } */
      // No negatividad - Los valores puntuales de ventas e inversión (marketing, comarketing y competencia) deben ser mayores o iguales a cero
      // Positividad - Los valores totales de ventas y de comarketing y competencia (estos dos pueden quedar vacíos) y los valores puntuales de saturación deben ser mayores a cero. Un medio de marketing puede tener inversión total cero.
      for (const medium of this.mediaSpend.ownControllable.data) {
        for (const value of medium.slice(2)) {
          if (Number(value) < 0) {
            this.errors.push({ type: 'error', sections: [3], text: `Media Spend values for ${medium[0]} must be non-negative.` })
          }
        }
      }
      for (const medium of this.mediaSpend.otherSpend.data) {
        let atLeastOneColumnHasData = false
        let mediumTotal = 0
        for (const value of medium.slice(2)) {
          if (value !== '') {
            atLeastOneColumnHasData = true
          }
          const val = Number(value)
          mediumTotal += val
          if (val < 0) {
            this.errors.push({ type: 'error', sections: [3], text: `Media Spend values for ${medium[0]} must be non-negative.` })
          }
        }
        if (atLeastOneColumnHasData && mediumTotal <= 0) {
          this.errors.push({ type: 'error', sections: [3], text: `Media Spend total for ${medium[0]} must be positive.` })
        }
      }
      let totalKpi = 0
      for (const value of this.kpi.data[0].slice(2)) {
        const val = Number(value)
        totalKpi += val
        if (val < 0) {
          this.errors.push({ type: 'error', sections: [6], text: 'KPI values must be non-negative.' })
          break
        }
      }
      if (totalKpi <= 0) {
        this.errors.push({ type: 'error', sections: [6], text: `KPI total must be positive.` })
      }
      for (const [index, item] of this.saturation.data.entries()) {
        if (index % 2 === 1) {
          for (const value of item.slice(4)) {
            if (Number(value) <= 0) {
              this.errors.push({ type: 'error', sections: [5], text: `Saturation values must be positive.` })
              break
            }
          }
        }
      }
      // Al menos un medio Own Controllable
      if (this.reference.media.length === 0) {
        this.errors.push({ type: 'error', sections: [2], text: 'The model must have Own Controlable Media' })
      }
      // Return based on existence of errors
      if (this.errors.length > 0 || this.warnings.length > 0) {
        return false
      } else {
        return true
      }
    },
    updatedAttributionsHandler () {
      console.log('updatedAttributionsHandler')
      const controllableMin = Number(this.attributions.data[0][1])
      const controllableMax = Number(this.attributions.data[0][2])
      const nonControllableMin = Number(this.attributions.data[1][1])
      const nonControllableMax = Number(this.attributions.data[1][2])
      const competitionMin = Number(this.attributions.data[2][1])
      const competitionMax = Number(this.attributions.data[2][2])

      const sumMax = controllableMax + nonControllableMax + competitionMax + Number(this.attributions.data[3][2]) + Number(this.attributions.data[4][2])
      const sumMin = controllableMin + nonControllableMin + competitionMin + Number(this.attributions.data[3][1]) + Number(this.attributions.data[4][1])
      console.log('PeriodicMin: ', Number(this.attributions.data[3][1]))
      console.log('SpotEventsMin: ', Number(this.attributions.data[4][1]))
      console.log('sumMax', sumMax)
      console.log('sumMin', sumMin)
      this.$set(this.attributions.data[5], 1, isNaN(sumMax) ? '0' : Math.round(100 - Math.min(100, sumMax)).toString())
      this.$set(this.attributions.data[5], 2, isNaN(sumMin) ? '0' : Math.round(100 - Math.max(0, sumMin)).toString())

      const kpiVector = this.kpi.data[0].slice(2).map(x => Number(x))
      const sumKpi = kpiVector.reduce((r, a) => r + a, 0)

      // Periodic/Cyclic Events
      const periodicCyclicVector = this.seasonalityAndSpotEvents.data[0].slice(2).map(x => Number(x))
      const periodicCyclicVariationRange = Number(this.seasonalityAndSpotEvents.data[0][1])
      const sumProductKpiPeriodicCyclicVector = kpiVector.reduce((r, a, i) => r + a * periodicCyclicVector[i], 0)
      console.log('sumProductKpiPeriodicCyclicVector: ', sumProductKpiPeriodicCyclicVector)
      console.log('sumKpi: ', sumKpi)
      console.log('periodicCyclicVariationRange: ', periodicCyclicVariationRange)
      const periodicCyclicMin = sumProductKpiPeriodicCyclicVector / sumKpi - periodicCyclicVariationRange
      console.log('periodicCyclicMin', periodicCyclicMin)
      this.$set(this.attributions.data[3], 1, isNaN(periodicCyclicMin) ? '0' : Math.round(periodicCyclicMin).toString())
      const periodicCyclicMax = sumProductKpiPeriodicCyclicVector / sumKpi + periodicCyclicVariationRange
      this.$set(this.attributions.data[3], 2, isNaN(periodicCyclicMax) ? '0' : Math.round(periodicCyclicMax).toString())
      // Spot Events
      const spotEventsVector = this.seasonalityAndSpotEvents.data[1].slice(2).map(x => Number(x))
      const spotEventsVariationRange = Number(this.seasonalityAndSpotEvents.data[1][1])
      const sumProductKpiSpotEventsVector = kpiVector.reduce((r, a, i) => r + a * spotEventsVector[i], 0)
      const spotEventsMin = sumProductKpiSpotEventsVector / sumKpi - spotEventsVariationRange
      this.$set(this.attributions.data[4], 1, isNaN(spotEventsMin) ? '0' : Math.round(spotEventsMin).toString())
      const spotEventsMax = sumProductKpiSpotEventsVector / sumKpi + spotEventsVariationRange
      this.$set(this.attributions.data[4], 2, isNaN(spotEventsMax) ? '0' : Math.round(spotEventsMax).toString())
    },
    closeDialog (saveToLocalStorage) {
      if (saveToLocalStorage && this.reference.media.length > 0) {
        const draft = {
          reference: this.reference,
          futureTrends: this.futureTrends,
          attributions: this.attributions,
          seasonalityAndSpotEvents: this.seasonalityAndSpotEvents,
          kpi: this.kpi,
          saturation: this.saturation,
          decay: this.decay,
          mediaSpend: this.mediaSpend,
          minHalfLife: this.minHalfLife,
          maxHalfLife: this.maxHalfLife
        }
        localStorage.setItem('ekrmDraft', JSON.stringify(draft))
      } else {
        localStorage.removeItem('ekrmDraft')
      }
      this.$emit('close-dialog')
    },
    removeMedia (item) {
      this.mediaInternal = this.mediaInternal.filter(mediaItem => mediaItem !== item)
    },
    createReferenceModel () {
      if (this.validateAll()) {
      // if (true) {
        this.saveJSON()
        this.closeDialog()
        localStorage.removeItem('ekrmDraft')
      } else {
        this.errorDialog = true
      }
    },
    saveJSON () {
      let obj = {
        reference: this.reference,
        media_spend: {
          own_controllable: {},
          other: {
            own_non_controllable_and_comarketing: {
              variation_range: this.mediaSpend.otherSpend.data[0][1],
              spend: this.mediaSpend.otherSpend.data[0].slice(2)
            },
            competition: {
              variation_range: this.mediaSpend.otherSpend.data[1][1],
              spend: this.mediaSpend.otherSpend.data[1].slice(2)
            }
          }
        },
        decay: {},
        saturation: {},
        kpi: {},
        seasonality_and_spot_events: {},
        attributions: {},
        future_trends: {}
      }
      for (let i = 0; i < this.reference.media.length; i++) {
        obj.media_spend.own_controllable[this.mediaSpend.ownControllable.data[i][0]] = {
          variation_range: this.mediaSpend.ownControllable.data[i][1],
          spend: this.mediaSpend.ownControllable.data[i].slice(2)
        }
        obj.decay[this.reference.media[i]] = {
          months_to_maximum_effect: {
            min: this.decay[this.reference.media[i]].maximumEffect[0],
            max: this.decay[this.reference.media[i]].maximumEffect[1]
          },
          months_to_half_life: {
            min: this.decay[this.reference.media[i]].halfLife[0],
            max: this.decay[this.reference.media[i]].halfLife[1]
          }
        }
        const j = 2 * i // For saturation there are 2 rows per medium, first one is row zero
        obj.saturation[this.saturation.data[j][0]] = {
          variation_range: this.saturation.data[j][1],
          saturation_point: this.saturation.data[j][3],
          spend: Array.apply(null, Array(10)).map((x, k) => { return (k + 1) * this.saturation.data[j][3] * 1.2 / 10 }),
          revenue: this.saturation.data[j + 1].slice(4)
        }
        obj.kpi = {
          avg_monthly_revenue: {
            variation_range: this.kpi.data[0][1],
            revenue: this.kpi.data[0].slice(2)
          }
        }
        obj.seasonality_and_spot_events = {
          periodic_cyclic_events_attribution: {
            variation_range: this.seasonalityAndSpotEvents.data[0][1],
            attribution: this.seasonalityAndSpotEvents.data[0].slice(2)
          },
          spot_events_attribution: {
            variation_range: this.seasonalityAndSpotEvents.data[1][1],
            attribution: this.seasonalityAndSpotEvents.data[1].slice(2)
          }
        }
        obj.attributions = {
          own_controllable_media_spend: {
            min: this.attributions.data[0][1],
            max: this.attributions.data[0][2]
          },
          own_non_controllable_and_comarketing_media_spend: {
            min: this.attributions.data[1][1],
            max: this.attributions.data[1][2]
          },
          competition_media_spend: {
            min: this.attributions.data[2][1],
            max: this.attributions.data[2][2]
          },
          periodic_cyclic_events: {
            min: this.attributions.data[3][1],
            max: this.attributions.data[3][2]
          },
          spot_events: {
            min: this.attributions.data[4][1],
            max: this.attributions.data[4][2]
          },
          base: {
            min: this.attributions.data[5][1],
            max: this.attributions.data[5][2]
          }
        }
        obj.future_trends = {
          sales: {
            min: this.futureTrends.data[0][1],
            max: this.futureTrends.data[0][2]
          },
          own_controllable_media_spend: {
            min: this.futureTrends.data[1][1],
            max: this.futureTrends.data[1][2]
          },
          own_non_controllable_and_comarketing_media_spend: {
            min: this.futureTrends.data[2][1],
            max: this.futureTrends.data[2][2]
          },
          competition_media_spend: {
            min: this.futureTrends.data[3][1],
            max: this.futureTrends.data[3][2]
          }
        }
      }
      this.createEkrm({
        /* definition: {
          "reference": {
            "category": "Test",
            "tier": "Reference 1",
            "market": "CL",
            "media": ["Audio", "Test"]
          },
          "media_spend": {
            "own_controllable": {
              "Audio": {
                "variation_range": 10,
                "spend": [1000, 1000, 1000, 1000, 1000, 1000, 1000, 1000, 1000, 500, 1000, 1000]
              },
              "Test": {
                "variation_range": 10,
                "spend": [1000, 1000, 1000, 1000, 1000, 1000, 1000, 1000, 1000, 500, 1000, 1000]
              }
            },
            "other": {
              "own_non_controllable_and_comarketing": {
                "variation_range": 10,
                "spend": [1000, 1000, 1000, 1000, 1000, 1000, 1000, 1000, 1000, 500, 1000, 1000]
              },
              "competition": {
                "variation_range": 10,
                "spend": [1000, 1000, 1000, 1000, 1000, 1000, 1000, 1000, 1000, 500, 1000, 1000]
              }
            }
          },
          "decay": {
            "Audio": {
              "months_to_maximum_effect": {
                "min": 0,
                "max": 2
              },
              "months_to_half_life": {
                "min": 0,
                "max": 3
              }
            },
            "Test": {
              "months_to_maximum_effect": {
                "min": 0,
                "max": 2
              },
              "months_to_half_life": {
                "min": 0,
                "max": 3
              }
            }
          },
          "saturation": {
            "Audio": {
              "variation_range": 10,
              "spend": [120, 240, 360, 480, 600, 720, 840, 960, 1080, 1200],
              "revenue": [200, 400, 600, 700, 800, 900, 1000, 1100, 1111, 1111]
            },
            "Test": {
              "variation_range": 10,
              "spend": [120, 240, 360, 480, 600, 720, 840, 960, 1080, 1200],
              "revenue": [200, 400, 600, 700, 800, 900, 1000, 1100, 1111, 1111]
            }
          },
          "kpi": {
            "avg_monthly_revenue": {
              "variation_range": 10,
              "revenue": [1000, 1000, 1000, 2000, 1000, 1000, 1000, 1000, 1000, 500, 1000, 1000]
            }
          },
          "seasonality_and_spot_events": {
            "periodic_cyclic_events_attribution": {
              "variation_range": 10,
              "attribution": [10, 10, 10, 10, 10, 10, 10, 0, 10, 0, 10, 10]
            },
            "spot_events_attribution": {
              "variation_range": 10,
              "attribution": [10, 10, 10, 10, 10, 10, 10, 0, 10, 0, 10, 10]
            }
          },
          "attributions": {
            "own_controllable_media_spend": {
              "min": 10,
              "max": 20
            },
            "own_non_controllable_and_comarketing_media_spend": {
              "min": 10,
              "max": 20
            },
            "competition_media_spend": {
              "min": 10,
              "max": 20
            },
            "periodic_cyclic_events": {
              "min": -1,
              "max": 19
            },
            "spot_events": {
              "min": -1,
              "max": 19
            },
            "base": {
              "min": 2,
              "max": 72
            }
          },
          "future_trends": {
            "sales": {
              "min": 10,
              "max": 20
            },
            "own_controllable_media_spend": {
              "min": 10,
              "max": 20
            },
            "own_non_controllable_and_comarketing_media_spend": {
              "min": 10,
              "max": 20
            },
            "competition_media_spend": {
              "min": 10,
              "max": 20
            }
          }
        }, */
        definition: obj,
        originalDefinition: JSON.stringify({
          reference: this.reference,
          futureTrends: this.futureTrends,
          attributions: this.attributions,
          seasonalityAndSpotEvents: this.seasonalityAndSpotEvents,
          kpi: this.kpi,
          saturation: this.saturation,
          decay: this.decay,
          mediaSpend: this.mediaSpend,
          minHalfLife: this.minHalfLife,
          maxHalfLife: this.maxHalfLife,
          // Carried through so a clone of a multi-year clone keeps its column labels.
          months: this.months
        }),
        createdBy: this.createdBy,
        createdById: this.createdById
      }).then(() => {
        console.log(JSON.stringify(obj))
        localStorage.removeItem('ekrmDraft')
      })
    },
    addCustomMedia: function () {
      if (!this.media.includes(this.addCustomMediaName)) {
        this.media.push(this.addCustomMediaName)
        this.mediaInternal.push(this.addCustomMediaName)
        this.addMediaDialog = false
        this.addCustomMediaName = ''
      } else {
        console.log('Error: Media name already in use')
      }
    },
    initializeColumnHeaders () {
      // One title per month column the grids actually have, which is twelve for a form built here
      // and 12·N for a multi-year clone reopened through the clone button. Every loop below walks
      // its own column array rather than a constant, so a grid of either width is relabelled whole.
      const monthHeaders = this.monthsNamesArray
      const titleMonths = function (columns) {
        for (let i = 0; i + 2 < columns.length; i++) {
          columns[i + 2].title = monthHeaders[i]
        }
      }
      // Future Trends columns
      this.futureTrends.columns[1].title = this.$t('components.ekrm.CreateEkrm.min')
      this.futureTrends.columns[2].title = this.$t('components.ekrm.CreateEkrm.max')
      // Attributions columns
      this.attributions.columns[1].title = this.$t('components.ekrm.CreateEkrm.min')
      this.attributions.columns[2].title = this.$t('components.ekrm.CreateEkrm.max')
      // Seasonality and Spot Events columns
      this.seasonalityAndSpotEvents.columns[1].title = this.$t('components.ekrm.CreateEkrm.variationRangePercent')
      titleMonths(this.seasonalityAndSpotEvents.columns)
      // KPI columns
      this.kpi.columns[1].title = this.$t('components.ekrm.CreateEkrm.variationRangePercent')
      titleMonths(this.kpi.columns)
      // Saturation columns
      this.saturation.columns[0].title = this.$t('components.ekrm.CreateEkrm.media')
      this.saturation.columns[1].title = this.$t('components.ekrm.CreateEkrm.revenueVariationRange')
      this.saturation.columns[2].title = this.$t('components.ekrm.CreateEkrm.maximumSpend')
      this.saturation.columns[3].title = this.$t('components.ekrm.CreateEkrm.saturationPoint')
      // Media Spend - Own Controllable columns
      this.mediaSpend.ownControllable.columns[0].title = this.$t('components.ekrm.CreateEkrm.ownControllableMedia')
      this.mediaSpend.ownControllable.columns[1].title = this.$t('components.ekrm.CreateEkrm.variationRangePercent')
      titleMonths(this.mediaSpend.ownControllable.columns)
      // Media Spend - Other Spend columns
      this.mediaSpend.otherSpend.columns[0].title = this.$t('components.ekrm.CreateEkrm.other')
      this.mediaSpend.otherSpend.columns[1].title = this.$t('components.ekrm.CreateEkrm.variationRangePercent')
      titleMonths(this.mediaSpend.otherSpend.columns)
      // Row names - Future Trends
      this.futureTrends.data[0][0] = this.$t('components.ekrm.CreateEkrm.salesRevenue')
      this.futureTrends.data[1][0] = this.$t('components.ekrm.CreateEkrm.ownControllableMediaSpendTotal')
      this.futureTrends.data[2][0] = this.$t('components.ekrm.CreateEkrm.ownNonControllableMediaSpendTotal')
      this.futureTrends.data[3][0] = this.$t('components.ekrm.CreateEkrm.competitionSpendTotal')
      // Row names - Attributions
      this.attributions.data[0][0] = this.$t('components.ekrm.CreateEkrm.ownControllableMediaSpendTotal')
      this.attributions.data[1][0] = this.$t('components.ekrm.CreateEkrm.ownNonControllableMediaSpendTotal')
      this.attributions.data[2][0] = this.$t('components.ekrm.CreateEkrm.competitionSpendTotal')
      this.attributions.data[3][0] = this.$t('components.ekrm.CreateEkrm.periodicCyclicEvents')
      this.attributions.data[4][0] = this.$t('components.ekrm.CreateEkrm.spotEvents')
      this.attributions.data[5][0] = this.$t('components.ekrm.CreateEkrm.base')
      // Row names - Seasonality & Spot Events
      this.seasonalityAndSpotEvents.data[0][0] = this.$t('components.ekrm.CreateEkrm.percentSalesAttributedPeriodic')
      this.seasonalityAndSpotEvents.data[1][0] = this.$t('components.ekrm.CreateEkrm.percentSalesAttributedSpot')
      // Row names - KPI
      this.kpi.data[0][0] = this.$t('components.ekrm.CreateEkrm.avgMonthlyRevenue')
      // Row names - Media Spend Other
      this.mediaSpend.otherSpend.data[0][0] = this.$t('components.ekrm.CreateEkrm.ownNonControllableMediaTotal')
      this.mediaSpend.otherSpend.data[1][0] = this.$t('components.ekrm.CreateEkrm.competitionTotal')
    }
  },
  computed: {
    ...mapGetters('settings', [
      'countries'
    ]),
    customMediaRules () {
      return [
        value => !!value.match('^[A-Za-z0-9][A-Za-z0-9 ]*[A-Za-z0-9]$') || this.$t('components.ekrm.CreateEkrm.customMediaValidation')
      ]
    },
    sections () {
      return [
        this.$t('components.ekrm.CreateEkrm.introduction'),
        this.$t('components.ekrm.CreateEkrm.reference'),
        this.$t('components.ekrm.CreateEkrm.mediaSpend'),
        this.$t('components.ekrm.CreateEkrm.decay'),
        this.$t('components.ekrm.CreateEkrm.saturation'),
        this.$t('components.ekrm.CreateEkrm.kpi'),
        this.$t('components.ekrm.CreateEkrm.seasonalityAndSpotEvents'),
        this.$t('components.ekrm.CreateEkrm.attributions'),
        this.$t('components.ekrm.CreateEkrm.futureTrends')
      ]
    },
    // How many months this form's grids carry. Twelve for anything built here, and 12·N for an EKRM
    // copied from a model over several calendar years and reopened through the clone button. Read
    // off the data rather than off `months` below, so an EKRM saved before any of this existed —
    // which carries no months key at all — still reports its true width.
    monthCount () {
      const row = this.kpi.data && this.kpi.data[0]
      return row && row.length > 2 ? row.length - 2 : 12
    },
    // The column titles, and the x axis of every chart on this page. `months` comes from the clone
    // being reopened and names the real calendar months; without it the bare month names, repeated
    // and numbered per year if a wide grid ever arrives without them, since three columns all
    // called January would be worse than three called "January 2".
    monthsNamesArray () {
      const t = key => this.$t(`components.ekrm.CreateEkrm.${key}`)
      if (this.months && this.months.length === this.monthCount) {
        return monthTitlesFor(t, this.months)
      }
      const names = monthTitlesFor(t, null)
      const titles = []
      for (let i = 0; i < this.monthCount; i++) {
        const name = names[i % 12]
        titles.push(this.monthCount > 12 ? `${name} ${Math.floor(i / 12) + 1}` : name)
      }
      return titles
    },
    fullWidth () {
      if (this.$vuetify.breakpoint.width > 1904) {
        return '1761px'
      } else if (this.$vuetify.breakpoint.width > 1264) {
        return '1161px'
      } else if (this.$vuetify.breakpoint.width > 960) {
        return '876px'
      } else {
        return 660
      }
    },
    maxOwnControllableMediaSpends () {
      console.log('Updated Own Controllable Media Spend')
      console.log(this.updatedMediaSpend)
      let maxSpends = []
      for (const row of this.mediaSpend.ownControllable.data) {
        maxSpends.push(Math.max(...row.slice(2)))
      }
      return maxSpends
    },
    chartDataSpotEvents () {
      console.log('Updated SeasonalityAndSpotEvents')
      console.log(this.updatedSeasonalityAndSpotEvents)
      let data = {
        '% of Sales Attributed to Spot Events': {
          x: this.monthsNamesArray,
          y: this.seasonalityAndSpotEvents.data[1].slice(2)
        }
      }
      return data
    },
    chartDataSeasonality () {
      console.log('Updated SeasonalityAndSpotEvents')
      console.log(this.updatedSeasonalityAndSpotEvents)
      let data = {
        '% of Sales Attributed to Periodic/Cyclic Events': {
          x: this.monthsNamesArray,
          y: this.seasonalityAndSpotEvents.data[0].slice(2)
        }
      }
      return data
    },
    chartDataKpi () {
      console.log('Updated Kpi')
      console.log(this.updatedKpi)
      let data = {
        KPI: {
          x: this.monthsNamesArray,
          y: this.kpi.data[0].slice(2)
        },
        Lower: {},
        Higher: {}
      }
      const variationRange = Number(this.kpi.data[0][1])
      data.Lower = {
        x: data.KPI.x,
        y: data.KPI.y.map(item => { return item !== '' ? Number(item) * (100 - variationRange) / 100 : '' })
      }
      data.Higher = {
        x: data.KPI.x,
        y: data.KPI.y.map(item => { return item !== '' ? Number(item) * (100 + variationRange) / 100 : '' })
      }
      return data
    },
    chartDataSaturation () {
      let data = {}
      console.log('Updated Saturation')
      console.log(this.updatedSaturation)
      for (let index in this.reference.media) {
        const j = 2 * index
        const medium = this.reference.media[index]

        // Check if saturation data exists and is properly initialized
        if (!this.saturation.data ||
            !this.saturation.data[j] ||
            !this.saturation.data[j + 1] ||
            this.saturation.data[j][3] === undefined ||
            this.saturation.data[j][3] === '') {
          // Initialize with empty/default data if not available
          data[medium] = {
            Revenue: { x: [0], y: [0] },
            Lower: { x: [0], y: [0] },
            Higher: { x: [0], y: [0] }
          }
          continue
        }

        const saturationPoint = Number(this.saturation.data[j][3]) || 1
        const revenueData = this.saturation.data[j + 1].slice(4)

        data[medium] = {
          Revenue: {
            x: [0].concat(Array.apply(null, Array(10)).map((x, k) => {
              return (k + 1) * saturationPoint * 1.2 / 10
            })),
            y: [0].concat(revenueData)
          },
          Lower: {},
          Higher: {}
        }

        const variationRange = Number(this.saturation.data[j][1]) || 0
        data[medium].Lower = {
          x: data[medium].Revenue.x,
          y: data[medium].Revenue.y.map(item => {
            return item !== '' && !isNaN(item) ? Number(item) * (100 - variationRange) / 100 : 0
          })
        }
        data[medium].Higher = {
          x: data[medium].Revenue.x,
          y: data[medium].Revenue.y.map(item => {
            return item !== '' && !isNaN(item) ? Number(item) * (100 + variationRange) / 100 : 0
          })
        }
      }
      return data
    },
    chartDataMediaSpend () {
      let data = {}
      console.log('Updated mediaSpend')
      console.log(this.updatedMediaSpend)
      for (let index in this.mediaSpend.otherSpend.data) {
        data[this.mediaSpend.otherSpend.data[index][0]] = {
          x: this.monthsNamesArray,
          y: this.mediaSpend.otherSpend.data[index].slice(2).map(item => {
            return Number(item)
          })
        }
      }
      for (let index in this.mediaSpend.ownControllable.data) {
        data[this.mediaSpend.ownControllable.data[index][0]] = {
          x: this.monthsNamesArray,
          y: this.mediaSpend.ownControllable.data[index].slice(2).map(item => {
            return Number(item)
          })
        }
      }
      return data
    },
    chartColorsMediaSpend () {
      let colors = {}
      Object.keys(this.chartDataMediaSpend).map((key, index) => {
        colors[key] = this.colors[index]
      })
      return colors
    }
  },
  components: {
    Spreadsheet,
    DecayRange,
    RiverChart,
    ChannelCurvesChartMulti,
    StackedBarChart
  },
  props: {
    createdBy: {
      type: String,
      required: true
    },
    createdById: {
      type: String,
      required: true
    },
    editing: {
      type: Object,
      required: false
    },
    cloning: {
      type: Boolean,
      required: false
    }
  },
  watch: {
    step () {
      if (Number(this.step) === 2) {
        this.$nextTick(() => {
          setTimeout(() => {
            this.$refs.textFieldCategory.$refs.input.focus()
          })
        })
      }
    },
    addMediaDialog () {
      if (this.addMediaDialog === true) {
        this.$nextTick(() => {
          setTimeout(() => {
            this.$refs.textFieldCustomMedia.$refs.input.focus()
          })
        })
      }
    },
    /* eslint-disable no-unused-vars */
    'mediaInternal': {
      immediate: true,
      handler: function (new_, old) {
        let addedMedia = []
        let removedMedia = []
        if (old && old.length !== new_.length) {
          addedMedia = new_.filter((item) => !old.includes(item))
          removedMedia = old.filter((item) => !new_.includes(item))
        }

        // Initialize decay objects for all media items first
        this.mediaInternal.forEach(element => {
          if (!this.decay[element]) {
            this.$set(this.decay, element, {
              maximumEffect: [0, 2],
              halfLife: [0, 3]
            })
            this.$set(this.minHalfLife, element, 0)
            this.$set(this.maxHalfLife, element, 3)
          }
        })

        // Remove decay data for removed media
        removedMedia.forEach(element => {
          this.$delete(this.decay, element)
          this.$delete(this.minHalfLife, element)
          this.$delete(this.maxHalfLife, element)
        })

        let mediaSpendData = []
        let saturationData = []
        let saturationRow = 1
        let saturationMergeCells = {}
        let saturationDisabledRows = []

        // Ensure we have at least an empty array to prevent undefined access
        if (this.mediaInternal.length === 0) {
          this.$set(this.mediaSpend.ownControllable, 'data', [])
          this.$set(this.saturation, 'data', [])
          this.$set(this.saturation, 'mergeCells', {})
          this.$set(this.saturation, 'disabledRows', [])
          this.$set(this.reference, 'media', [])
          return
        }

        this.mediaInternal.forEach(element => {
          let existingElement
          for (let index = 0; index < this.mediaSpend.ownControllable.data.length; index++) {
            if (this.mediaSpend.ownControllable.data[index][0] === element) {
              existingElement = this.mediaSpend.ownControllable.data[index]
            }
          }
          if (existingElement) {
            mediaSpendData.push(existingElement)
          } else {
            // Initialize with proper empty structure for new media. Sized off the grid rather than
            // written out, because a multi-year clone reopened here has 12*N month columns and a
            // fixed twelve-month row would emit a spend array shorter than the KPI's — which the
            // EKRM refuses outright. The saturation rows below are NOT this: their fourteen cells
            // are four leading columns plus the ten grid positions, and never widen.
            const row = [element, '']
            for (let month = 0; month < this.monthCount; month++) {
              row.push('')
            }
            mediaSpendData.push(row)
          }

          // Saturation - ensure we handle both new and existing elements properly
          let existingSaturationFound = false

          if (!addedMedia.includes(element) && !removedMedia.includes(element) && this.saturation.data) {
            // Pre-existing elements that must be kept
            for (let index = 0; index < this.saturation.data.length; index += 2) {
              if (this.saturation.data[index] && this.saturation.data[index][0] === element) {
                saturationData.push([element, this.saturation.data[index][1] || '', this.saturation.data[index][2] || '', this.saturation.data[index][3] || '', `=(D${saturationRow})*1.2/10`, `=(E${saturationRow}+D${saturationRow}*1.2/10)`, `=(F${saturationRow}+D${saturationRow}*1.2/10)`, `=(G${saturationRow}+D${saturationRow}*1.2/10)`, `=(H${saturationRow}+D${saturationRow}*1.2/10)`, `=(I${saturationRow}+D${saturationRow}*1.2/10)`, `=(J${saturationRow}+D${saturationRow}*1.2/10)`, `=(K${saturationRow}+D${saturationRow}*1.2/10)`, `=(L${saturationRow}+D${saturationRow}*1.2/10)`, `=(M${saturationRow}+D${saturationRow}*1.2/10)`])
                // Ensure the revenue row exists and has proper structure
                if (this.saturation.data[index + 1]) {
                  saturationData.push([...this.saturation.data[index + 1]])
                } else {
                  saturationData.push(['', '', '', '', '', '', '', '', '', '', '', '', '', ''])
                }
                existingSaturationFound = true
                break
              }
            }
          }

          if (!existingSaturationFound) {
            // New elements or missing data - initialize with proper empty structure
            saturationData.push([element, '', '', '', `=(D${saturationRow})*1.2/10`, `=(E${saturationRow}+D${saturationRow}*1.2/10)`, `=(F${saturationRow}+D${saturationRow}*1.2/10)`, `=(G${saturationRow}+D${saturationRow}*1.2/10)`, `=(H${saturationRow}+D${saturationRow}*1.2/10)`, `=(I${saturationRow}+D${saturationRow}*1.2/10)`, `=(J${saturationRow}+D${saturationRow}*1.2/10)`, `=(K${saturationRow}+D${saturationRow}*1.2/10)`, `=(L${saturationRow}+D${saturationRow}*1.2/10)`, `=(M${saturationRow}+D${saturationRow}*1.2/10)`])
            // Initialize revenue row with empty values
            saturationData.push(['', '', '', '', '', '', '', '', '', '', '', '', '', ''])
          }

          // Populate table structure for each media channel
          saturationMergeCells[`A${saturationRow}`] = [1, 2]
          saturationMergeCells[`B${saturationRow}`] = [1, 2]
          saturationMergeCells[`C${saturationRow}`] = [1, 2]
          saturationMergeCells[`D${saturationRow}`] = [1, 2]
          saturationDisabledRows.push(saturationRow - 1)
          saturationRow += 2
        })

        // Commit all temporary objects
        this.$set(this.mediaSpend.ownControllable, 'data', mediaSpendData)
        this.$set(this.saturation, 'data', saturationData)
        this.$set(this.saturation, 'mergeCells', saturationMergeCells)
        this.$set(this.saturation, 'disabledRows', saturationDisabledRows)

        // Always commit this.mediaInternal, even the first time this.mediaInternal is set
        // This is now safe because decay objects are initialized above
        this.$set(this.reference, 'media', this.mediaInternal)
      }
    },
    updatedSeasonalityAndSpotEvents: function () {
      this.updatedAttributionsHandler()
    },
    updatedKpi: function () {
      this.updatedAttributionsHandler()
    },
    maxOwnControllableMediaSpends: function () {
      // Update Max Spends in saturation Spreadsheet
      for (const [index, item] of this.maxOwnControllableMediaSpends.entries()) {
        if (this.saturation.data && this.saturation.data[2 * index]) {
          this.$set(this.saturation.data[2 * index], 2, item >= 0 ? item.toString() : '')
        }
      }
    }
  },
  created () {
    console.log(this.editing)
    let draft = null
    if (this.cloning && this.editing.originalDefinition) {
      this.draft = this.editing.originalDefinition
      draft = JSON.parse(this.editing.originalDefinition)
      draft.reference.category = `${draft.reference.category} (copy)`
    } else {
      this.draft = localStorage.getItem('ekrmDraft')
      if (this.draft) {
        draft = JSON.parse(this.draft)
      }
    }
    if (draft) {
      // Before the grids, because every column title below is derived from it.
      this.months = Array.isArray(draft.months) ? draft.months : null
      // Reference
      this.$set(this.reference, 'category', draft.reference.category)
      this.$set(this.reference, 'tier', draft.reference.tier)
      this.$set(this.reference, 'market', draft.reference.market)
      for (const medium of draft.reference.media) {
        if (!this.media.includes(medium)) {
          this.addCustomMediaName = medium
          this.addCustomMedia()
        } else {
          this.mediaInternal.push(medium)
        }
      }
      // Future Trends
      this.futureTrends = draft.futureTrends
      // Attributions
      this.attributions = draft.attributions
      // Seasonality and Spot Events
      this.seasonalityAndSpotEvents = draft.seasonalityAndSpotEvents
      // KPI
      this.kpi = draft.kpi
      // Saturation
      this.saturation = draft.saturation
      // Decay
      this.decay = draft.decay
      this.minHalfLife = draft.minHalfLife
      this.maxHalfLife = draft.maxHalfLife
      // Media Spend
      this.mediaSpend = draft.mediaSpend
      /* for (const item of draft.mediaSpend.ownControllable.data) {
        this.mediaSpend.ownControllable.data.push(item)
      }
      this.mediaSpend.otherSpend.data.splice(0, this.mediaSpend.otherSpend.data.length)
      for (const item of draft.mediaSpend.otherSpend.data) {
        this.mediaSpend.otherSpend.data.push(item)
      } */
    }
    // Initialize column headers with translations
    this.initializeColumnHeaders()
  }
}
</script>

<style scoped>
  li {
    margin-top: 5px;
  }
</style>
